import Analysis from 'domain/entity/analysis';
import ReadAnalysisCriteria from 'use-case/analysis/read/readAnalysisCriteria';
import { UpdateAnalysis, updateAnalysisUseCase } from 'use-case/analysis/update/updateAnalysisUseCase';
import StoredAnalysis from 'domain/entity/storedAnalysis';
import LicensePermissions from 'domain/entity/LicensePermissions';
import { Magnitude } from 'domain/entity/magnitude';
import PresetPage from 'domain/entity/preset/presetPage/presetPage';
import { GetUseCase } from 'use-case/genericUseCase';
import { CreateAnalysis, createAnalysisUseCase } from 'use-case/analysis/create/createAnalysisUseCase';
import deleteDashboardAnalysisUseCase, {
    DeleteDashboardAnalysis,
} from 'use-case/analysis/deleteDashboardAnalysis/deleteDashboardAnalysisUseCase';
import saveToDashboardUseCase, { SaveToDashboard } from 'use-case/analysis/saveToDashboard/saveToDashboardUseCase';
import getDashboardUseCase, { GetDashboard } from 'use-case/dashboard/get/getDashboardUseCase';
import getDashboardsUseCase, { GetDashboards } from 'use-case/dashboards/get/getDashboardsUseCase';
import ReadMagnitudesCriteria from 'use-case/magnitudes/read/readMagnitudesCriteria';
import ReadPresetPageCriteria from 'use-case/presets/presetPage/read/readPresetPageCriteria';
import { CreateStoredAnalysis, createStoredAnalysisUseCase } from 'use-case/storedAnalysis/create/createStoredAnalysisUseCase';
import deleteStoredAnalysisUseCase, { DeleteStoredAnalysis } from 'use-case/storedAnalysis/delete/deleteStoredAnalysisUseCase';
import duplicateStoredAnalysisUseCase, { DuplicateStoredAnalysis } from 'use-case/storedAnalysis/duplicate/duplicateStoredAnalysisUseCase';
import ReadStoredAnalysisCriteria from 'use-case/storedAnalysis/read/readStoredAnalysisCriteria';
import {
    UpdateStarStoredAnalysis,
    updateStarStoredAnalysisUseCase,
} from 'use-case/storedAnalysis/update/star/updateStarStoredAnalysisUseCase';
import { UpdateNameStoredAnalysis, updateNameStoredAnalysisUseCase } from 'use-case/storedAnalysis/update/updateNameStoredAnalysisUseCase';
import getReadAnalysisUseCase from 'use-case/analysis/read/readAnalysisUseCase';
import getReadLicensePermissionsUseCase from 'use-case/licensePermissions/read/readLicensePermissionsuseCase';
import getReadMagnitudesUseCase from 'use-case/magnitudes/read/readMagnitudesUseCase';
import getReadPresetPageUseCase from 'use-case/presets/presetPage/read/readPresetPageUsecase';
import getReadStoredAnalysisUseCase from 'use-case/storedAnalysis/read/readStoredAnalysisUseCase';
import createDependencyContext from 'adapter/in/common/dependenciesContext';
import createAnalysisApiClient from 'adapter/out/client/analysis/createAnalysisApiClient';
import deleteDashboardAnalysisApiClient from 'adapter/out/client/analysis/deleteDashboardAnalysisApiClient';
import getReadAnalysisApiClient from 'adapter/out/client/analysis/readAnalysisApiClient';
import updateAnalysisApiClient from 'adapter/out/client/analysis/updateAnalysisApiClient';
import getDashboardApiClient from 'adapter/out/client/dashboard/getDashboardApiClient';
import getDashboardsApiClient from 'adapter/out/client/dashboards/getDashboardsApiClient';
import readLicensePermissionsApiClient from 'adapter/out/client/licensePermissions/readLicensePermissionsApiClient';
import getReadMagnitudesApiClient from 'adapter/out/client/magnitudes/readMagnitudesApiClient';
import getReadPresetPageApiClient from 'adapter/out/client/presetPage/readPresetPageApiClient';
import createStoredAnalysisApiClient from 'adapter/out/client/storedAnalysis/create/createStoredAnalysisApiClient';
import deleteStoredAnalysisApiClient from 'adapter/out/client/storedAnalysis/delete/deleteStoredAnalysisApiClient';
import duplicateStoredAnalysisApiClient from 'adapter/out/client/storedAnalysis/duplicate/duplicateStoredAnalysisApiClient';
import getReadStoredAnalysisApiClient from 'adapter/out/client/storedAnalysis/read/readStoredAnalysisApiClient';
import updateNameStoredAnalysisApiClient from 'adapter/out/client/storedAnalysis/update/updateNameStoredAnalysisApiClient';
import updateStarStoredAnalysisApiClient from 'adapter/out/client/storedAnalysis/update/updateStarStoredAnalysisApiClient';
import saveToDashboardApiClient from 'adapter/out/client/analysis/sendToDashboardApiClient';
import previewAnalysisDataUseCase, { PreviewAnalysisData } from 'use-case/analysis/data/preview/previewAnalysisDataUseCase';
import previewAnalysisDataApiClient from 'adapter/out/client/analysis/previewAnalysisDataApiClient';
import readLayoutsUseCase, { ReadLayouts } from 'use-case/layout/read/readLayoutsUseCase';
import readLayoutsApiClient from 'adapter/out/client/layout/readLayoutsApiClient';
import updateLayoutUseCase, { UpdateLayout } from 'use-case/layout/update/updateLayoutUseCase';
import updateLayoutApiClient from 'adapter/out/client/layout/updateLayoutApiClient';

export interface IAdvancedAnalyticsDependencies {
    getReadAnalysis: GetUseCase<Analysis, ReadAnalysisCriteria>;
    getUpdateAnalysis: UpdateAnalysis;
    getCreateStoredAnalysis: CreateStoredAnalysis;
    getCreateAnalysis: CreateAnalysis;
    getReadStoredAnalysis: GetUseCase<StoredAnalysis, ReadStoredAnalysisCriteria>;
    getReadDashboards: GetDashboards;
    getSaveToDashboard: SaveToDashboard;
    getUpdateNameStoredAnalysis: UpdateNameStoredAnalysis;
    getUpdateStarStoredAnalysis: UpdateStarStoredAnalysis;
    getDuplicateStoredAnalysis: DuplicateStoredAnalysis;
    getDeleteStoredAnalysis: DeleteStoredAnalysis;
    getReadDashboard: GetDashboard;
    getDeleteDashboardAnalysis: DeleteDashboardAnalysis;
    getReadMagnitudes: GetUseCase<Magnitude[], ReadMagnitudesCriteria>;
    getReadLicensePermissions: GetUseCase<LicensePermissions, null>;
    getReadPresetPage: GetUseCase<PresetPage, ReadPresetPageCriteria>;
    previewAnalysisData: PreviewAnalysisData;
    getReadLayouts: ReadLayouts;
    getUpdateLayout: UpdateLayout;
}
const AdvancedAnalyticsDependencyContext = createDependencyContext<IAdvancedAnalyticsDependencies>();

export const getAdvancedAnalyticsDependencies = (
    fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>,
    baseUrl: string
) => ({
    getReadAnalysis: getReadAnalysisUseCase(getReadAnalysisApiClient(fetch, baseUrl)),
    getUpdateAnalysis: updateAnalysisUseCase(updateAnalysisApiClient(fetch, baseUrl)),
    getCreateStoredAnalysis: createStoredAnalysisUseCase(createStoredAnalysisApiClient(fetch, baseUrl)),
    getReadStoredAnalysis: getReadStoredAnalysisUseCase(getReadStoredAnalysisApiClient(fetch, baseUrl)),
    getReadDashboards: getDashboardsUseCase(getDashboardsApiClient(fetch, baseUrl)),
    getSaveToDashboard: saveToDashboardUseCase(saveToDashboardApiClient(fetch, baseUrl)),
    getUpdateNameStoredAnalysis: updateNameStoredAnalysisUseCase(updateNameStoredAnalysisApiClient(fetch, baseUrl)),
    getUpdateStarStoredAnalysis: updateStarStoredAnalysisUseCase(updateStarStoredAnalysisApiClient(fetch, baseUrl)),
    getDuplicateStoredAnalysis: duplicateStoredAnalysisUseCase(duplicateStoredAnalysisApiClient(fetch, baseUrl)),
    getDeleteStoredAnalysis: deleteStoredAnalysisUseCase(deleteStoredAnalysisApiClient(fetch, baseUrl)),
    getCreateAnalysis: createAnalysisUseCase(createAnalysisApiClient(fetch, baseUrl)),
    getReadDashboard: getDashboardUseCase(getDashboardApiClient(fetch, baseUrl)),
    getDeleteDashboardAnalysis: deleteDashboardAnalysisUseCase(deleteDashboardAnalysisApiClient(fetch, baseUrl)),
    getReadMagnitudes: getReadMagnitudesUseCase(getReadMagnitudesApiClient(fetch, baseUrl)),
    getReadLicensePermissions: getReadLicensePermissionsUseCase(readLicensePermissionsApiClient(fetch, baseUrl)),
    getReadPresetPage: getReadPresetPageUseCase(getReadPresetPageApiClient(fetch, baseUrl)),
    previewAnalysisData: previewAnalysisDataUseCase(previewAnalysisDataApiClient(fetch, baseUrl)),
    getReadLayouts: readLayoutsUseCase(readLayoutsApiClient(fetch, baseUrl)),
    getUpdateLayout: updateLayoutUseCase(updateLayoutApiClient(fetch, baseUrl)),
});

export const {
    Provider: AdvancedAnalyticsDependenciesContextProvider,
    useDependencies: useAdvancedAnalyticsDependencies,
} = AdvancedAnalyticsDependencyContext;
